import { Lock, LockOpen } from "lucide-react"
import { useEffect, useState } from "react"

import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"

import type { EstadoCofre } from "./tipos"

/**
 * Si el cofre está apagado, cerrado o abierto, y cuánto le queda.
 *
 * ⚠ **El reloj vive AQUÍ y no en la página.** Late cada segundo; puesto en la página,
 * cada latido volvería a pintar todas las fichas del cofre para mover dos dígitos.
 *
 * La cuenta sale de `segundos` y del momento en que se pidió el estado: el servidor
 * manda el TTL real y aquí sólo se descuenta, sin preguntar cada segundo.
 */
export function EstadoDelCofre({
  estado,
  pedidoEn,
  cerrando,
  onCerrar,
}: {
  estado: EstadoCofre | undefined
  pedidoEn: number
  cerrando: boolean
  onCerrar: () => void
}) {
  const [ahora, setAhora] = useState(() => Date.now())
  const abierto = estado?.abierto === true

  useEffect(() => {
    if (!abierto) return
    const reloj = window.setInterval(() => setAhora(Date.now()), 1000)
    return () => window.clearInterval(reloj)
  }, [abierto])

  if (estado === undefined) return null

  if (!estado.configurado) {
    return (
      <p
        role="alert"
        className="mb-4 rounded-[var(--radius-control)] border px-3 py-2 text-sm"
        style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
      >
        El cofre está apagado: {estado.motivo} Mientras tanto no se pueden guardar ni ver
        contraseñas; lo demás de cada ficha sí.
      </p>
    )
  }

  const quedan = abierto
    ? Math.max(0, Math.round((pedidoEn + estado.segundos * 1000 - ahora) / 1000))
    : 0

  if (quedan === 0) {
    return (
      <p className="mb-4 flex items-center gap-2 text-sm" style={{ color: "var(--fg-muted)" }}>
        <Lock size={16} aria-hidden={true} />
        Cofre cerrado: al mostrar una contraseña se te pedirá la tuya.
      </p>
    )
  }

  const minutos = Math.floor(quedan / 60)
  const segundos = String(quedan % 60).padStart(2, "0")
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
      <Insignia tono="aviso" icono={<LockOpen size={14} aria-hidden={true} />}>
        Cofre abierto · se cierra en{" "}
        <span className="tabular-nums">
          {minutos}:{segundos}
        </span>
      </Insignia>
      <Boton tamano="sm" onClick={onCerrar} disabled={cerrando}>
        Cerrar ahora
      </Boton>
    </div>
  )
}
